<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'

interface Feature {
  name: string
  description: string
  image: string
}

const props = defineProps<{
  items: Feature[]
}>()

const activeIndex = ref(0)
const isAutoScrolling = ref(true)
const container = ref<HTMLElement | null>(null)
let interval: ReturnType<typeof setInterval> | null = null

const startAutoScroll = () => {
  if (interval) clearInterval(interval)
  interval = setInterval(() => {
    if (isAutoScrolling.value) {
      activeIndex.value = (activeIndex.value + 1) % props.items.length
    }
  }, 10000)
}

const stopAutoScroll = () => {
  isAutoScrolling.value = false
}

const selectFeature = (index: number) => {
  activeIndex.value = index
  stopAutoScroll()
}

onMounted(() => {
  startAutoScroll()
})

onUnmounted(() => {
  if (interval) clearInterval(interval)
})

useIntersectionObserver(
  container,
  ([{ isIntersecting }]) => {
    if (!isIntersecting) {
      isAutoScrolling.value = true
    }
  },
  { threshold: 0.1 }
)
</script>

<template>
  <div
    ref="container"
    class="bg-[#03120d] border border-white/10 flex flex-col lg:flex-row gap-5 p-5 rounded-[44px] w-full overflow-hidden"
  >
    <!-- Left side: Features list -->
    <div class="flex flex-col gap-3 w-full lg:w-[354px] shrink-0">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="relative cursor-pointer transition-all duration-300"
        @click="selectFeature(index)"
      >
        <!-- Card background for active state -->
        <div
          v-if="activeIndex === index"
          class="absolute inset-0 rounded-[20px] overflow-hidden pointer-events-none"
        >
          <div
            class="absolute inset-0 bg-cover bg-center mix-blend-luminosity opacity-20"
            :style="{ backgroundImage: `url(${item.image})` }"
          />
          <div class="absolute inset-0 bg-[#1dffce]/20 mix-blend-color" />
          <div class="absolute inset-0 bg-black/50" />
          <div class="absolute inset-0 shadow-[inset_0px_0.75px_0.75px_2.25px_rgba(96,255,149,0.4)]" />
        </div>

        <div
          class="border-[1.5px] border-white/10 flex gap-4 h-[100px] items-center p-4 rounded-[20px] relative transition-opacity duration-300"
          :class="activeIndex === index ? 'opacity-100' : 'opacity-60'"
        >
          <!-- Indicator bar -->
          <div class="h-[68px] w-1 bg-white/20 rounded-full relative shrink-0">
            <div
              v-if="activeIndex === index"
              class="absolute inset-0 bg-gradient-to-t from-[#14e646] to-[#117128] rounded-full"
            />
          </div>

          <!-- Text content -->
          <div class="flex flex-col gap-1 overflow-hidden">
            <p
              class="font-base font-semibold text-lg text-white tracking-[0.36px]"
              :class="activeIndex === index ? '' : 'truncate'"
            >
              {{ item.name }}
            </p>
            <p class="font-base font-medium text-sm text-[#b5b5b5] line-clamp-2 leading-tight tracking-[0.14px]">
              {{ item.description }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Right side: Placeholder Image -->
    <div class="flex-grow bg-white/5 rounded-[32px] overflow-hidden relative min-h-[300px] lg:min-h-0">
      <Transition
        enter-active-class="transition duration-500 ease-out"
        enter-from-class="opacity-0 translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-300 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <div
          :key="activeIndex"
          class="absolute inset-0 flex items-center justify-center p-8"
        >
          <ImagePlaceholder class="w-full h-full object-contain" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.font-display {
  font-family: 'Clash Grotesk Variable', sans-serif;
}

/* Ensure the line-clamp works */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
